import React, { useEffect, useState, Fragment } from "react";
import { connect } from "react-redux";
import { compose } from "recompose";
import { useRouter } from "next/router";
import { createStructuredSelector } from "reselect";
import { calculationsFor1to8M, useFormattedMessage, useSideModalScrollToTop } from "hooks";
import cn from "classnames";
import * as yup from "yup";
import { withFormik } from "formik";
import Input from "components/Input";
import TwoCellRow from "components/TwoCellRow";
import CustomFormatNumber from "components/CustomFormatNumber";
import PrevButton from "components/PrevButton";
import NextButton from "components/NextButton";
import { numberRegex } from "constants/validationRegex";
import {
    selectInvestmentAmountResult,
    selectInvestmentFailure,
    selectInvestmentStepData,
    selectIsInvestmentFailed,
} from "store/user/investment/selectors";
import { selectProjectType1to8M, selectProjectDetails } from "store/project/selectors";
import {
    createInvestMentDataFailure,
    fetchTotatInvestmentAmountStart,
    setInvestmentStepData,
} from "store/user/investment/actions";
import InvestmentChecks from "components/1to8MillionInvestments/InvestmentChecks";
import { RESPONSE_ERRORS } from "constants/index";
import Alert from "components/Alert";

const messages = {
    requiredField: "required_field",
    investmentSumFormDigitalSharesInvalid: "form_digital_shares_invalid",
    investmentSumFormMinimumShares: "minimum_shares",
    investmentSumFormMinimum5kShares: "minimum_shares_5k",
    investmentSumFormMinimum25kShares: "minimum_shares_25k",
    investmentSumFormMinimum100kShares: "minimum_shares_100k",
    investmentSumDividable100kShares: "shares_dividable_100k",
    investmentSumMorethanNumberOfShares: "form_digital_shares_grater",
    alreadyFunded: "alread_finded",
    investText: "invest_text",
    perShare: "per_share",
    heading: "continue_investment_heading",
    label: "how_many_digital_shares",
    placeholder: "from_five_hundred_shares",
    inputSymbol: "no",
    pricePerShare: "property_price_per_share",
    investmentAmount: "your_investment_amount",
    minInvestShares1: "min_invest_shares1",
    minInvestShares2: "min_invest_shares2",
    minInvestmentSumPlaceholder1: "invest_from",
    minInvestmentSumPlaceholder2: "invest_digital_shares",
    loadingButtonText: "please_wait_button",
    investmnetChangedInMeantime: "update_your_information",
    investmentSumFormMax25k: "investment_sum_max25k",
    investmentSumFormMax50k: "investment_sum_max50k",
    investmentSumFormMax100k: "investment_sum_max100k",
    investmentAccordanceEwpg: "investment_accordance_ewpg",
    investmentAccordanceEwpgDescription: "investment_accordance_ewpg_description",
    maxInvestAmountReached: "maximum_investment_amount_reached",
    interestedInHigherInvestment: "interested_in_higher_investment",
};

// messages.backButtonText = backButtonText

const InvestmentSum = ({
    projectID,
    investmentStepData,
    className,
    setInvestmentStepData,
    numberOfShares,
    projectData = [],
    setFieldTouched,
    handleBlur,
    handleSubmit,
    values,
    setFieldValue,
    touched,
    errors,
    validateForm,
    submitForm,
    comeFrom,
    continueShares,
    isInitInvestmentLoading,
    backSlideEffect = "",
    setBackSlideEffect,
    minInvestShare,
    maxInvestAmount,
    // dividableBy,
    initError,
    isInvestment1to8M,
    fetchTotalInvestmentAmount,
    investmentAmountResult = {},
    open,
    createInvestmentError,
    setCreateInvestMentDataFailure,
    isEwPgInvestmentProcess,
}) => {
    const { investmentAmountData, isAmountloading } = investmentAmountResult;
    useSideModalScrollToTop();

    const router = useRouter();
    const [investmnetChangedError, setInvestmnetChangedError] = useState("");
    const [showCheckboxes, setShowCheckboxes] = useState(false);

    useEffect(() => {
        validateForm();
    }, [values, touched]);

    const { formatMessage } = useFormattedMessage();

    const pricePerShare = 1;
    const [selectedSum, setSum] = useState(
        (comeFrom !== "cancel" && continueShares * pricePerShare) ||
            (investmentStepData.investSum && investmentStepData.investSum.sum) ||
            0,
    );

    useEffect(() => {
        comeFrom !== "cancel" && setSum(continueShares * pricePerShare);
    }, [comeFrom, continueShares]);

    useEffect(() => {
        if (isInvestment1to8M && open) fetchinvestmentAmountData();
    }, [isInvestment1to8M, open]);

    useEffect(() => {
        if (isInvestment1to8M && initError == RESPONSE_ERRORS.SELF_DECLARATION_ERROR) {
            fetchinvestmentAmountData();
            setInvestmnetChangedError("investmnetChangedInMeantime");
        } else if (isInvestment1to8M && createInvestmentError == RESPONSE_ERRORS.SELF_DECLARATION_ERROR) {
            setInvestmnetChangedError("investmnetChangedInMeantime");
        } else {
            setInvestmnetChangedError("");
        }
    }, [initError, createInvestmentError]);

    useEffect(() => {
        if (isInvestment1to8M) {
            const totalAmount = values.digitalShares * pricePerShare + investmentAmountData;
            const { isMoreThan1k, isMoreThan10k } = calculationsFor1to8M(totalAmount, values, maxInvestAmount);
            setFieldValue("isMoreThan1k", isMoreThan1k);
            setFieldValue("isMoreThan10k", isMoreThan10k);
            setFieldValue("totalInvestmentAmount", totalAmount);

            // To show the checkboxes accroding to the entered digital shares
            values.digitalShares >= minInvestShare && values.digitalShares <= totalAmount
                ? setShowCheckboxes(true)
                : setShowCheckboxes(false);
        }
    }, [values.digitalShares, investmentAmountData]);

    useEffect(() => {
        if (isInvestment1to8M) {
            setFieldValue("moreThan100kFreelyDisposable", false);
            setFieldValue("notMoreThanDoubleMonthlyIncome", false);
        }
    }, [values.digitalShares]);

    const fetchinvestmentAmountData = () => {
        fetchTotalInvestmentAmount(projectID);
    };

    const fetchedProjects = projectData;
    let project = null;
    if (router.query && router.query.id && fetchedProjects && Array.isArray(fetchedProjects) && fetchedProjects.length) {
        project = fetchedProjects.find((item) => item.projectID === router.query.id);
    }

    const address = `${project && project.address && project.address.street}, ${
        project && project.address && project.address.city
    }`;
    const totalAmount = values.digitalShares * pricePerShare + investmentAmountData;

    // hook to get conditional values for 1 to 8 million investment
    const { isMoreThan1k, isMoreThan10k, is1KConditionIncomeplete, is10kConditionIncomeplete } = calculationsFor1to8M(
        totalAmount,
        values,
        maxInvestAmount,
    );

    const isButtonDisableWith1to8M = isInvestment1to8M
        ? isAmountloading || is1KConditionIncomeplete || is10kConditionIncomeplete
        : false;

    const sharesErrorMessage = (_minInvestShare) => {
        if (investmnetChangedError) {
            return formatMessage(messages.investmnetChangedInMeantime);
        }
        if (errors.totalInvestmentAmount) {
            return formatMessage(messages[errors.totalInvestmentAmount]);
        }
        if (errors.digitalShares) {
            if (errors.digitalShares == "investmentSumMorethanNumberOfShares") {
                return `${formatMessage(messages[errors.digitalShares])} ${numberOfShares}`;
            }
            if (errors.digitalShares == "minInvestShares") {
                return `${formatMessage(messages.minInvestShares1)} ${_minInvestShare} ${formatMessage(
                    messages.minInvestShares2,
                )}`;
            }
            return formatMessage(messages[errors.digitalShares]);
        }
        return null;
    };

    return (
        <>
            <div className={`${className} investment-process-container investment-sum ${backSlideEffect}`}>
                <div className="shadow investment-property-block flex mb-10">
                    <img
                        className="property-image"
                        src={
                            project && project.images && project.images.teaserImage
                                ? `${project.images.teaserImage.path}?h=180`
                                : null
                        }
                        alt=""
                    />
                    <div className="property-details-block">
                        <h5
                            className="my-0 property-title line-height-initial property-item-text-overflow mt-2 mb-1"
                            title={project && project.title}
                        >
                            {project && project.title}
                        </h5>
                        <span className="text-16 fw-500 property-country property-item-text-overflow" title={address}>
                            {address}
                        </span>
                        {/* {project && project.projectID !== "DE-B-01" ? ( */}
                        <span className="text-14 fw-600 d-block property-fund">
                            {project && project.fundingPercentage}% {formatMessage(messages.alreadyFunded)}
                        </span>
                        {/* ) : null} */}
                        <div className="flex justify-between align-center buy-share-div">
                            <span className="text-14 buy-btn mb-0">{formatMessage(messages.investText)}</span>
                            <span className="text-14 fw-600 share">
                                <CustomFormatNumber value={1} /> {formatMessage(messages.perShare)}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="your-investment">
                    <h4>{formatMessage(messages.heading)}</h4>
                    <form method="post" onSubmit={handleSubmit}>
                        <Input
                            disabled={investmentAmountData == maxInvestAmount}
                            readOnly={investmentAmountData == maxInvestAmount}
                            className="mb-5"
                            label={formatMessage(messages.label)}
                            inputClassName="text-input form-item investment-share-input"
                            type="text"
                            name="digitalShares"
                            placeholder={`${formatMessage(messages.minInvestmentSumPlaceholder1)}${minInvestShare}${formatMessage(
                                messages.minInvestmentSumPlaceholder2,
                            )}`}
                            inputSymbol={formatMessage(messages.inputSymbol)}
                            value={values.digitalShares || (selectedSum && selectedSum / pricePerShare) || ""}
                            onChange={(value) => {
                                setFieldTouched("digitalShares", true);
                                setFieldValue("digitalShares", value.target.value);
                                createInvestmentError && setCreateInvestMentDataFailure(null);
                                const sum = value.target.value * pricePerShare;
                                sum <= numberOfShares ? (sum ? setSum(sum) : setSum(0)) : setSum(0);
                            }}
                            onBlur={handleBlur}
                            errorMessage={sharesErrorMessage(minInvestShare)}
                            error={
                                (touched.digitalShares && (errors.digitalShares || errors.totalInvestmentAmount)) ||
                                investmnetChangedError
                            }
                        />
                        {isInvestment1to8M &&
                            (investmentAmountData == maxInvestAmount || values.totalInvestmentAmount == maxInvestAmount) &&
                            (() => {
                                const isDataMax = investmentAmountData == maxInvestAmount;
                                const isTotalMax = values.totalInvestmentAmount == maxInvestAmount;
                                const labelClass = isDataMax ? "alert-red-color" : isTotalMax ? "light-grey-color" : "";

                                return (
                                    <label className={`text-left mt-0 mb-2 ${labelClass}`}>
                                        {formatMessage(messages.maxInvestAmountReached)}
                                        {maxInvestAmount}
                                        {formatMessage(messages.interestedInHigherInvestment)}
                                    </label>
                                );
                            })()}
                        <div className="border-x" />
                        <div className="share-section">
                            <TwoCellRow
                                removeLClass
                                removeRClass
                                containerClassname="mb-2 align-center"
                                rClassname="text-16 mb-4 fw-600 value"
                                left={formatMessage(messages.pricePerShare)}
                                right={pricePerShare ? <CustomFormatNumber value={pricePerShare} /> : ""}
                            />
                            <TwoCellRow
                                removeLClass
                                removeRClass
                                containerClassname="align-center"
                                rClassname="text-16 mb-4 fw-600 value"
                                left={formatMessage(messages.investmentAmount)}
                                right={selectedSum || selectedSum === 0 ? <CustomFormatNumber value={selectedSum} /> : ""}
                            />
                        </div>
                        {values.digitalShares && (isMoreThan10k || isMoreThan1k) && isInvestment1to8M && showCheckboxes && (
                            <InvestmentChecks
                                moreThan100kFreelyDisposable={values.moreThan100kFreelyDisposable}
                                notMoreThanDoubleMonthlyIncome={values.notMoreThanDoubleMonthlyIncome}
                                isMoreThan10k={isMoreThan10k}
                                isMoreThan1k={isMoreThan1k}
                                setFieldValue={setFieldValue}
                                isLoading={isInitInvestmentLoading || isAmountloading}
                            />
                        )}
                    </form>
                    {isEwPgInvestmentProcess && (
                        <Alert
                            className="mt-15"
                            title={formatMessage(messages.investmentAccordanceEwpg)}
                            description={formatMessage(messages.investmentAccordanceEwpgDescription)}
                        />
                    )}
                </div>
            </div>
            <div className={`bottom-bar-container ${continueShares === "" && "single-button"}`}>
                {continueShares !== "" && (
                    <PrevButton
                        onClick={() => {
                            setInvestmentStepData({ step: "renderContinueData" });
                            setBackSlideEffect("step-slide-right");
                        }}
                        disable={isInitInvestmentLoading}
                    />
                )}
                <NextButton
                    className={cn({
                        "mw-100 mr-1": continueShares === "",
                        "disable-grey": Object.keys(errors).length || investmentAmountData == maxInvestAmount,
                        "init-investment-button": true,
                    })}
                    title={isAmountloading ? formatMessage(messages.loadingButtonText) : null}
                    onClick={() => submitForm()}
                    disable={isInitInvestmentLoading || isButtonDisableWith1to8M || investmentAmountData == maxInvestAmount}
                    isLoading={isInitInvestmentLoading || isAmountloading}
                />
            </div>
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    investmentStepData: selectInvestmentStepData,
    projectData: selectProjectDetails,
    initError: selectInvestmentFailure,
    isInvestment1to8M: selectProjectType1to8M,
    investmentAmountResult: selectInvestmentAmountResult,
    createInvestmentError: selectIsInvestmentFailed,
});

const mapDispatchToProps = (dispatch) => ({
    setInvestmentStepData: (data) => dispatch(setInvestmentStepData(data)),
    fetchTotalInvestmentAmount: (value) => dispatch(fetchTotatInvestmentAmountStart(value)),
    setCreateInvestMentDataFailure: (value) => dispatch(createInvestMentDataFailure(value)),
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withFormik({
        enableReinitialize: true,
        mapPropsToValues: ({ comeFrom, continueShares, investmentStepData }) => {
            return {
                digitalShares: (comeFrom !== "cancel" && continueShares) || investmentStepData.investSum.digitalShares || "",
                isMoreThan1k: !!investmentStepData?.invest1to8MData?.isMoreThan1k,
                isMoreThan10k: !!investmentStepData?.invest1to8MData?.isMoreThan10k,
                moreThan100kFreelyDisposable:
                    investmentStepData?.invest1to8MData?.SelfDeclarationMoreThan100000FreelyDisposable == "true",
                notMoreThanDoubleMonthlyIncome:
                    investmentStepData?.invest1to8MData?.SelfDeclarationNotMoreThanDoubleMonthlyNetIncome == "true",
                totalInvestmentAmount: "",
            };
        },
        validateOnBlur: true,
        validateOnChange: true,
        validationSchema: (props) =>
            yup.object().shape({
                digitalShares: yup
                    .string()
                    .matches(numberRegex, "investmentSumFormDigitalSharesInvalid")
                    .test("digitalShares", "minInvestShares", (value) => {
                        return !!(value && value >= props.minInvestShare);
                    })
                    .test("digitalShares", "investmentSumDividable100kShares", (value) => {
                        return props.dividableBy ? !!(value && value % props.dividableBy === 0) : true;
                    })
                    .test("digitalShares", "investmentSumMorethanNumberOfShares", (value) => {
                        return !!(value && value <= props.numberOfShares);
                    })
                    .required("requiredField")
                    .nullable(true),
                totalInvestmentAmount: yup
                    .string()
                    .test(
                        "totalInvestmentAmount",
                        `${
                            props?.maxInvestAmount === 50000
                                ? "investmentSumFormMax50k"
                                : props.maxInvestAmount === 100000
                                  ? "investmentSumFormMax100k"
                                  : "investmentSumFormMax25k"
                        }`,
                        (totalInvestmentAmount = 0) => {
                            return props?.isInvestment1to8M ? parseFloat(totalInvestmentAmount) <= props?.maxInvestAmount : true;
                        },
                    ),
            }),
        handleSubmit: (values, { setSubmitting, props }) => {
            setSubmitting(false);
            props.onSubmit && props.onSubmit(values);
        },
        displayName: "InvestmentSumForm",
    }),
)(InvestmentSum);
